import { parseWildcardVariables } from 'ui/uiConfig/components/wildcardItems.helper'
import sentry from 'utils/sentry/sentry'
import { isCalcDatasetInstruction } from './dataset.calc.helper'
import { getReturnIdsFromDatasetInstruction } from './dataset.helper'

/**
 * Orders calculation dataset instructions based on their variable dependencies, ensuring that
 * each dataset is resolved only after all its required variables are available.
 *
 * - Returns calcs with their wildcard variables expanded; the given instructions are not changed.
 * - Leaves out calcs without a return_id, and logs them as a warning.
 * - Detects and logs missing variables that cannot be resolved from any dataset.
 * - Leaves out calcs that cannot be resolved: logs those on a dependency cycle as an error, and the
 *   rest, which wait on an input that is unavailable, as a warning.
 *
 * @param datasetInstructions - Array of dataset instructions, potentially including calculation datasets.
 * @param initialResolvedReturnIds - An array of return_ids that are already resolved.
 * @param options.isFiltered - The instructions are a filtered subset, so only dependency cycles are logged; the
 *   unfiltered view of the same config reports the rest.
 * @returns An array of calculation dataset instructions ordered by their dependency resolution.
 */
export function getDatasetInstructionsInExecutionOrder(
  datasetInstructions: DatasetInstruction[],
  initialResolvedReturnIds: string[],
  options?: { isFiltered?: boolean }
): CalcDatasetInstruction[] {
  const expandedInstructions = datasetInstructions.map(withWildcardVariablesExpanded)
  const unnamedCalcs = expandedInstructions.filter(isUnnamedCalc)
  if (unnamedCalcs.length && !options?.isFiltered) {
    sentry.captureScopedMessage({
      scope: 'dataset_calc',
      issueType: 'unnamed_calc',
      message: 'Calc dataset instructions without a return_id are left out',
      level: 'warning',
      extra: {
        datasetInstructionCount: datasetInstructions.length,
        unnamedCalcVariables: unnamedCalcs.map(({ contract }) => contract.variables),
      },
    })
  }

  const instructionsWithoutUnnamedCalcs = expandedInstructions.filter(
    (datasetInstruction) => !isUnnamedCalc(datasetInstruction)
  )
  const returnIdToVariables = mapReturnIdToVariables(instructionsWithoutUnnamedCalcs)
  const returnIdToDataset = mapReturnIdToDataset(instructionsWithoutUnnamedCalcs)

  // Check if any dataset variable is missing (skip when filtering is active, as missing sources are expected)
  const missingVariables = getMissingCalcVariables(instructionsWithoutUnnamedCalcs, returnIdToVariables)
  if (missingVariables.size && !options?.isFiltered) {
    sentry.captureScopedMessage({
      scope: 'dataset_calc',
      issueType: 'source_less_variables',
      message: `Calc dataset has source-less variables: ${Array.from(missingVariables).join(', ')}`,
      extra: {
        missingVariables: Array.from(missingVariables),
        datasetInstructionCount: datasetInstructions.length,
      },
    })
  }

  const resolvedReturnIds = new Set([...initialResolvedReturnIds, ...Array.from(missingVariables)])
  const { ordered, blocked } = orderByDependencies(returnIdToVariables, resolvedReturnIds)

  const cyclicReturnIds = getReturnIdsOnDependencyCycle(blocked)
  if (cyclicReturnIds.length) {
    sentry.captureScopedMessage({
      scope: 'dataset_calc',
      issueType: 'dependency_loop',
      message: `Dependency cycle in ${getDatasetInstructionsInExecutionOrder.name}`,
      level: 'error',
      extra: { datasetInstructionCount: datasetInstructions.length, cyclicReturnIds },
      fingerprint: ['dataset_calc', 'dependency_loop', getDatasetInstructionsInExecutionOrder.name],
    })
  }

  const skippedReturnIds = Object.keys(blocked).filter((returnId) => !cyclicReturnIds.includes(returnId))
  if (skippedReturnIds.length && !options?.isFiltered) {
    sentry.captureScopedMessage({
      scope: 'dataset_calc',
      issueType: 'unavailable_calc_inputs',
      message: `Calc datasets skipped for unavailable inputs: ${skippedReturnIds.join(', ')}`,
      level: 'warning',
      extra: {
        datasetInstructionCount: datasetInstructions.length,
        skippedReturnIds,
        unavailableVariables: getUnavailableInputs(blocked, skippedReturnIds),
      },
    })
  }

  return ordered.map((returnId) => returnIdToDataset[returnId])
}

function isUnnamedCalc(datasetInstruction: DatasetInstruction): datasetInstruction is CalcDatasetInstruction {
  return isCalcDatasetInstruction(datasetInstruction) && !datasetInstruction.return_id
}

function withWildcardVariablesExpanded(datasetInstruction: DatasetInstruction): DatasetInstruction {
  if (!isCalcDatasetInstruction(datasetInstruction)) {
    return datasetInstruction
  }

  const variables = parseWildcardVariables(datasetInstruction.contract.variables)
  return { ...datasetInstruction, contract: { ...datasetInstruction.contract, variables } }
}

function orderByDependencies(
  returnIdToVariables: Record<string, Set<string>>,
  initiallyResolved: Set<string>
): { ordered: string[]; blocked: Record<string, Set<string>> } {
  const ordered: string[] = []
  const blocked = { ...returnIdToVariables }
  const resolvedReturnIds = new Set(initiallyResolved)

  let lastPassOrderedAny = true
  while (lastPassOrderedAny && Object.keys(blocked).length) {
    const orderedBeforePass = ordered.length

    for (const returnId of Object.keys(blocked)) {
      const unresolvedVariables = new Set(
        Array.from(blocked[returnId]).filter((variable) => !resolvedReturnIds.has(variable))
      )

      if (unresolvedVariables.size) {
        blocked[returnId] = unresolvedVariables
      } else {
        resolvedReturnIds.add(returnId)
        delete blocked[returnId]
        ordered.push(returnId)
      }
    }
    lastPassOrderedAny = ordered.length > orderedBeforePass
  }

  return { ordered, blocked }
}

function getReturnIdsOnDependencyCycle(blocked: Record<string, Set<string>>): string[] {
  return Object.keys(blocked).filter((returnId) => {
    const reachable = new Set(blocked[returnId])
    for (const variable of reachable) {
      if (Object.hasOwn(blocked, variable)) blocked[variable].forEach((dependency) => reachable.add(dependency))
    }
    return reachable.has(returnId)
  })
}

function getUnavailableInputs(blocked: Record<string, Set<string>>, returnIds: string[]): string[] {
  const variables = returnIds.flatMap((returnId) => Array.from(blocked[returnId]))
  return Array.from(new Set(variables.filter((variable) => !Object.hasOwn(blocked, variable))))
}

function mapReturnIdToDataset(datasetInstructions: DatasetInstruction[]): Record<string, CalcDatasetInstruction> {
  return datasetInstructions
    .filter(isCalcDatasetInstruction)
    .reduce<Record<string, CalcDatasetInstruction>>((acc, datasetInstruction) => {
      return {
        ...acc,
        [datasetInstruction.return_id]: datasetInstruction,
      }
    }, {})
}

export function mapReturnIdToVariables(datasetInstructions: DatasetInstruction[]): Record<string, Set<string>> {
  return datasetInstructions
    .filter(isCalcDatasetInstruction)
    .reduce<Record<string, Set<string>>>((acc, { return_id, contract }) => {
      const variables = contract.variables.filter((variable) => typeof variable === `string`)
      return {
        ...acc,
        [return_id]: new Set(variables),
      }
    }, {})
}

export function getMissingCalcVariables(
  datasetInstructions: Partial<DatasetInstruction>[],
  returnIdToVariables: Record<string, Set<string>>
): Set<string> {
  const allReturnIds = new Set(
    datasetInstructions.map((datasetInstruction) => getReturnIdsFromDatasetInstruction(datasetInstruction)).flat()
  )
  const allVariables = new Set(
    Object.values(returnIdToVariables)
      .map((variables) => [...Array.from(variables)])
      .flat()
  )
  const missingVariables: Set<string> = new Set()

  allVariables.forEach((variable) => {
    // Skip unexpanded wildcard variables (e.g. "demands.fjv.*") — they are not truly
    // missing; they just haven't been expanded yet because elplan data isn't loaded.
    if (variable.includes('*')) {
      return
    }
    if (!allReturnIds.has(variable)) {
      missingVariables.add(variable)
    }
  })

  return missingVariables
}
